<template>
  <v-dialog v-model="show" max-width="850px">
    <v-card>
      <v-card-title class="h6 primary--text">
        <v-icon color="primary" left>mdi-gauge</v-icon> Vessel Consumption
        {{ activity.startDateTimeLt | removeTAndTime }}</v-card-title
      >
      <v-container>
        <TankConsumptionForm
          v-for="tank in vesselTanksWithStatus"
          :key="tank.id"
          :tank="tank"
          :transaction="tank.transaction"
          :onSubmit="submitConsumptionTransaction"
          :onSubmitEdit="submitConsumptionTransactionEdit"
          :onDelete="deleteConsumptionTransaction"
        />
      </v-container>
    </v-card>
  </v-dialog>
</template>

<script>
  import { mapGetters, mapState } from 'vuex';
  import dateTimeHelpers from '../../mixins/DateTimeHelpers';
  import TankStatusMixin from '../../mixins/TankStatusMixin';
  import BulkTransactionsHelpersMixin from '../../mixins/BulkTransactionsHelpersMixin';
  import TankConsumptionForm from './TankConsumptionForm.vue';
import { BulkTransactionCreateModel } from '../../models/bulk-transaction/BulkTransactionCreateModel';

  export default {
    name: 'VesselConsumptionActivityDialog',
    mixins: [BulkTransactionsHelpersMixin, TankStatusMixin],
    components: { TankConsumptionForm },
    props: {
      value: Boolean,
      activity: Object,
    },
    computed: {
      ...mapState(['voyageReportsStore', 'tanksStore']),
      ...mapGetters({
        getVesselTanksWithStatus: 'tanksStore/getVesselTanksWithStatusForVesselConsumption',
        activeSiteId: 'userStore/getActiveSiteId',
        getUnsyncedBulkTransactionsForActivity: 'dataSyncStore/getUnsyncedBulkTransactionsForActivity',
        isClientConnectedToServer: 'userStore/isClientConnectedToServer',
      }),
      show: {
        get() {
          return this.value;
        },
        set(value) {
          this.$emit('input', value);
        },
      },
      voyageReport() {
        return this.voyageReportsStore ? this.voyageReportsStore.voyageReportDetailsData : {};
      },
      bulkTransactions() {
        const { bulkTransactions } = this.activity;
        const result = [].concat(bulkTransactions)
          .concat(this.getUnsyncedBulkTransactionsForActivity);
        return result;
      },
      vesselTanksWithStatus() {
        const tanks = this.getVesselTanksWithStatus(this.voyageReport.vesselId)
          .map((x) => ({
            ...x,
            transaction: { ...this.getConsumptionTransactionForTank(x.id, this.activity) },
          }));
        return tanks;
      },
      isoDateString() {
        return dateTimeHelpers.dateTimeAsIsoDateString(new Date());
      },
    },
    methods: {
      initStateAndLoadDetails() {
        if (!this.isTankStateLoaded) {
          this.detailsLoading = true;
          this.$store.dispatch('tanksStore/loadTankStatuses', this.activeSiteId)
        }
      },
      loadTankDetails(tankId) {
        this.detailsLoading = true;
        return this.$store.dispatch('tanksStore/loadTankDetails', tankId)
          .finally(() => { this.detailsLoading = false; });
      },
      getConsumptionTransactionForTank(tankId) {
        let transaction = this.bulkTransactions
          .find((transaction) => transaction.tank && transaction.tank.id === tankId);

        if (!transaction) {
          transaction = {
            isTankUpdate: true,
            quantity: null,
          };
        }
        return transaction;
      },
      getConsumptionStatusForTank(tankId) {
        const transaction = this.getConsumptionTransactionForTank(tankId);

        if (!transaction)
          return null;

        const status = this.tanksStore.tankDetails.tankStatusHistory
          .find(x => x.bulkTransactionId === transaction.id
            || x.bulkTransactionIdc === transaction.idc);

        return status;

      },
      submitConsumptionTransaction(tank, transaction) {
        this.isSubmitting = true;

        const bulkTransactionCreateModel = new BulkTransactionCreateModel(
          this.activeSiteId,
          this.activity.endDateTimeLt,
          "Consumption",
          this.voyageReport.vesselName,
          this.activity.locationName,
          tank.currentTankStatus ? tank.currentTankStatus.bulk : tank.bulkName,
          parseFloat(transaction.quantity),
          tank.qtyUom,
          transaction.bulkDeliveryNoteNumber,
          transaction.comments,
          this.activity.id,
          tank.id);



        return this.$store.dispatch('bulkTransactionsStore/createBulkTransaction', bulkTransactionCreateModel)
          .then((transaction) => {
            this.onSuccess('Bulk Consumption Transaction Created', transaction);
            this.$emit('refreshSelectedConsumptionActivity', this.activity.id ?? this.activity.idc);
            return transaction;
          })
          .catch((err) => this.onError('Failed to Create Bulk Consumption Transaction', err));

      },
      submitConsumptionTransactionEdit(tank, transaction) {
        this.isSubmitting = true;

        const previousVersionId = transaction.id;

        const bulkTransactionCreateModel = new BulkTransactionCreateModel(
          this.activeSiteId,
          this.activity.endDateTimeLt,
          "Consumption",
          this.voyageReport.vesselName,
          this.activity.locationName,
          transaction.bulk,
          parseFloat(transaction.quantity),
          tank.qtyUom,
          transaction.bulkDeliveryNoteNumber,
          transaction.comments,
          this.activity.id,
          tank.id);

        bulkTransactionCreateModel.previousVersionId = previousVersionId;

        return this.loadTankDetails(tank.id).then(() => {
          return this.$store.dispatch('bulkTransactionsStore/createBulkTransaction', bulkTransactionCreateModel)
            .then((transaction) => {
              this.onSuccess('Bulk Consumption Transaction Created', transaction);
              this.$emit('refreshSelectedConsumptionActivity', this.activity.id ?? this.activity.idc);
              return transaction;
            })
            .catch((err) => this.onError('Failed to Edit Bulk Consumption Transaction', err));
        });


        //const completedDateTimeLt = this.activity.endDateTimeLt;
        //const transactionUpdate = this.getBulkTransactionUpdateModel(transaction);
        //console.log("Trans Update", completedDateTimeLt, transaction);
        //return;

        //transactionUpdate.completedDateTimeLt = completedDateTimeLt;

        //return this.loadTankDetails(tank.id).then(() => {
        //  return this.$store.dispatch('bulkTransactionsStore/updateBulkTransaction', transactionUpdate)
        //    .then((resp) => {
        //      this.onSuccess('Bulk Consumption Updated', resp);
        //      this.$emit('refreshSelectedConsumptionActivity', this.activity.id ?? this.activity.idc);
        //      return transaction;
        //    })
        //    .then((bulkTransaction) => {
        //      let newTankStatus;
        //      const consumptionStatus = this.getConsumptionStatusForTank(tank.id);

        //      if (consumptionStatus) {
        //        newTankStatus = this.updateTankStatusForBulkTransaction(
        //          bulkTransaction, consumptionStatus);
        //      }

        //      console.log("[New]", newTankStatus);
        //      console.log("[Cons]", consumptionStatus);

        //      if (newTankStatus && bulkTransaction) {
        //        newTankStatus.bulkTransactionId = bulkTransaction.id ?? null;
        //        newTankStatus.bulkTransactionIdc = bulkTransaction.idc ?? null;
        //        this.$store.dispatch('tanksStore/createTankStatus', { tankStatus: newTankStatus, manualRecording: false })
        //          .catch((err) => this.onError('Failed to Update Tank Status from Bulk Transaction', err));
        //      }
        //    })
        //    .catch((err) => this.onError('Failed to Edit Bulk Transaction', err));
        //})

      },
      deleteConsumptionTransaction(tank, transaction) {
        return this.$store.dispatch('bulkTransactionsStore/deleteBulkTransaction', transaction)
          .then((resp) => {
            this.onSuccess('Consumption Transaction Deleted', resp);
            this.$emit('refreshSelectedConsumptionActivity', this.activity.id ?? this.activity.idc);
          })
          .catch((err) => this.onError('Failed To Delete Bulk Transaction', err));
      },
      onSuccess(message, data) {
        this.$toast.success(message);
      },
      onError(message, data) {
        this.$toast.error(message);
      },
    },
    created() {
      this.initStateAndLoadDetails();
    },
    beforeDestroy() {
      this.$store.dispatch('tanksStore/clearTankDetails');
    },
  };
</script>
